import { EdgelessObject, type IFramework, type IResultOptions } from '@/framework';
import { MediaType, type IMediaLayerProps } from '@/version-types';
import { EdgelessGIFElement } from './edgeless-gif-element';
import { EdgelessVideoElement } from './edgeless-video-element';
import { type IEdgelessMediaLayer } from './types';

export { type IEdgelessMediaLayer } from './types';

const getPlayChangeEventName = (layerId: string) => `${layerId}-play-change`;

export class EdgelessMediaLayer extends EdgelessObject implements IEdgelessMediaLayer {
    declare public readonly mediaType: MediaType;
    declare public readonly mediaElement: EdgelessVideoElement | EdgelessGIFElement;
    private eventHandlers: number[] = [];

    isPlaying: boolean = false;

    constructor(layerId: string, framework: IFramework) {
        super(layerId, framework.service);

        const layer = this.service.flatLayer.getLayer(this.layerId);
        const props = layer.data as IMediaLayerProps;

        this.mediaType = props.mediaType;

        if (this.mediaType === MediaType.Video) {
            this.mediaElement = new EdgelessVideoElement(this);
        } else {
            this.mediaElement = new EdgelessGIFElement(this);
        }

        this.bindAutoPlay();
    }

    private bindAutoPlay() {
        // 非 Edgeless 模式下，自动播放
        if (this.isEdgelessMode) return;
        const { frame } = this.service;

        // 循环播放
        this.mediaElement.onLoop(() => {
            if (!this.isEdgelessMode) {
                if (this.videoEl) {
                    this.mediaElement.seek(0);
                    void this.mediaElement.play().catch((error) => {
                        console.error('EdgelessMediaLayer loop play error', error);
                    });
                }
                return true;
            }
        });

        const play = (mute = false) => {
            const activeFrameId = frame.getActiveFrameId();
            const layerFrameId = frame.getLayerFrameId(this.layerId);

            if (activeFrameId !== layerFrameId) {
                this.mediaElement.pause();
                return;
            }

            if (mute) {
                this.mediaElement.mute(mute);
            }
            void this.mediaElement.play().catch((error) => {
                console.error('EdgelessMediaLayer autoplay error', error);
            });
        };

        // 浏览器不支持自动播放非静音的视频
        play(true);

        // 监听激活画布索引变化，是否需要继续播放
        const handler = this.service.frame.onActiveIndexChange(() => play(true));
        this.eventHandlers.push(handler);
    }

    reportPause() {
        this.isPlaying = false;
        this.service.edgelessCanvas.plugins.media.remove(this);
        this.service.events.emit(getPlayChangeEventName(this.layerId), {
            isPlaying: false,
            ...this.mediaElement.getDurationAndUrl(),
        });
    }

    reportPlay() {
        this.isPlaying = true;
        this.service.edgelessCanvas.plugins.media.add(this);
        this.service.events.emit(getPlayChangeEventName(this.layerId), {
            isPlaying: true,
            ...this.mediaElement.getDurationAndUrl(),
        });
    }

    onChange(fn: (args: { isPlaying: boolean; duration: number; url: string }) => void) {
        return this.service.events.on(getPlayChangeEventName(this.layerId), fn);
    }

    get videoEl() {
        return this.mediaType === MediaType.Video ? (this.mediaElement as EdgelessVideoElement).videoEl : null;
    }

    async play() {
        return this.mediaElement.play();
    }

    pause() {
        return this.mediaElement.pause();
    }

    reset() {
        return this.mediaElement.reset();
    }

    seek(time: number) {
        return this.mediaElement.seek(time);
    }

    override resetResource() {
        return this.mediaElement.resetResource();
    }

    /** 媒体异步解码后持续换帧，演示期间需保持重绘 */
    override get requiresContinuousRender(): boolean {
        return true;
    }

    /**
     * 在导航缩略图中绘制媒体封面，保持主画布的播放资源与进度
     * @param ctx - 导航预览上下文，会写入封面像素及图层水印
     */
    override renderPreview(ctx: CanvasRenderingContext2D) {
        if (this.noRender || this.domRender || this.width <= 0 || this.height <= 0) return;
        const cover = this.mediaElement.getPreviewCover();
        ctx.drawImage(cover || this.service.edgelessCanvas.placeholderCanvas, 0, 0, this.width, this.height);
        this.service.svipWatermark.drawLayerWatermark(this, ctx);
    }

    override async loadResource() {
        await this.mediaElement.loadResource();
        return this.resource;
    }

    /** 重新加载媒体资源（URL 变化时调用） */
    async reloadResource() {
        const wasPlaying = this.isPlaying;
        if (wasPlaying) {
            // console.log('reloadResource pause');
            this.pause();
        }
        await this.mediaElement.reloadResource();

        // 非 Edgeless 模式下，自动播放
        if (!this.isEdgelessMode) {
            void this.play().catch((error) => {
                console.error('EdgelessMediaLayer reload play error', error);
            });
        }

        // 资源更新后触发 canvas 重绘
        this.service.edgelessCanvas.requestRenderAll();
    }

    override dispose() {
        this.eventHandlers.forEach((handler) => {
            this.service.events.off(handler);
        });
        this.eventHandlers = [];
        this.reportPause();
        this.mediaElement.dispose();
        this.resource = null;
    }

    override shouldSkipRender() {
        return super.shouldSkipRender() || (this.isEdgelessMode && this.isPlaying);
    }

    override shouldSkipFocusRender() {
        return this.isEdgelessMode && this.isPlaying;
    }

    override active() {
        if (!this.isEdgelessMode) return null;

        if (this.mediaType === MediaType.Video) {
            this.mediaElement.loadResource(false).then(() => {
                this.service.edgelessCanvas.requestRenderAll();
                void this.play().catch((error) => {
                    console.error('EdgelessMediaLayer active play error', error);
                });
            });
            return this;
        }
        void this.play().catch((error) => {
            console.error('EdgelessMediaLayer active GIF play error', error);
        });
        return this;
    }

    override inactive() {
        if (!this.isEdgelessMode) return null;

        if (this.mediaType === MediaType.Video) {
            this.pause();
            // console.log('inactive loadResource');
            this.mediaElement.setRenderCover();
            this.mediaElement.loadResource(true).then(() => {
                this.service.edgelessCanvas.requestRenderAll();
            });
            return this;
        }
        this.pause();
        return this;
    }

    override supportsUrlResult() {
        return true;
    }

    override async getResult(args: IResultOptions = {}) {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        const data = layer.data as IMediaLayerProps;
        const url = args.aiWatermark ? layer.extraInfo?.watermarkURL || data.url : data.url;
        return url ? { url } : {};
    }

    private get isEdgelessMode() {
        return this.service.edgelessCanvas.isEdgelessMode;
    }
}
